import React, { useState } from 'react';
import Grid from '@mui/material/Grid';
import OutlinedInput from '@mui/material/OutlinedInput';
import { Select, MenuItem, Paper, Button, Box, Collapse, IconButton, Typography } from '@mui/material';
import InputLabel from '@mui/material/InputLabel';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import SpeedDial from '@mui/material/SpeedDial';
import SpeedDialIcon from '@mui/material/SpeedDialIcon';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import FilterListIcon from '@mui/icons-material/FilterList';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import DefaultSelectClient from '../../../components/default-select-client';
import ContractDataTable, { IContractDataTableProps } from './components/contract-data-table';
import { useContractSearchController } from './context/contract-search-controller';
import { useContractSearchState } from './context/contract-search-context';
import PageHeader from '../../../components/page-header';

export function ContractSearchComponent() {
    const [filtersOpen, setFiltersOpen] = useState(false);

    const {
        handleOnChange,
        handleSearch,
        handleClearFilters,
        navigateToCreate
    } = useContractSearchController();

    const {
        typeContract,
        categoryProcess,
        parameters,
        searchTrigger
    } = useContractSearchState();

    const dataTableProperties: IContractDataTableProps = {
        parameters: parameters,
        searchTrigger: searchTrigger
    };

    return (
        <React.Fragment>
            <Grid item xs={12} md={12} lg={12}>
                <PageHeader
                    title="Consulta de Contratos"
                    subtitle="Pesquise e gerencie todos os contratos cadastrados no sistema"
                    breadcrumbs={[
                        { label: 'Contratos' }
                    ]}
                />
            </Grid>

            <Grid item xs={12} md={12} lg={12}>
                <Paper
                    sx={{
                        p: 2,
                        display: 'flex',
                        flexDirection: 'column'
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            mb: filtersOpen ? 2 : 0,
                            cursor: 'pointer',
                            '&:hover': {
                                backgroundColor: 'action.hover'
                            },
                            borderRadius: 1,
                            p: 1,
                            ml: -1,
                            mr: -1
                        }}
                        onClick={() => setFiltersOpen(!filtersOpen)}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <FilterListIcon color="action" />
                            <Typography variant="h6" component="h2">
                                Filtros
                            </Typography>
                        </Box>
                        <IconButton
                            size="small"
                            sx={{ pointerEvents: 'none' }}
                        >
                            {filtersOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                    </Box>

                    <Collapse in={filtersOpen}>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <Grid container spacing={1.5}>
                                <Grid item lg={12} sm={12} xl={12}>
                                    <InputLabel>Buscar por</InputLabel>
                                    <OutlinedInput
                                        name="input-term"
                                        fullWidth
                                        value={parameters.term || ''}
                                        onChange={handleOnChange}
                                    />
                                </Grid>
                                <Grid item lg={5} sm={5} xl={5}>
                                    <InputLabel>Tipo de Contrato</InputLabel>
                                    <Select
                                        fullWidth
                                        name="select-contract-type"
                                        value={parameters.contractTypeId || ''}
                                        onChange={handleOnChange}
                                    >
                                        <MenuItem value="">
                                            <em>Selecionar</em>
                                        </MenuItem>
                                        {typeContract.map((element) => {
                                            return (
                                                <MenuItem value={element.id.toString()} key={element.id}>
                                                    {element.id} - {element.name}
                                                </MenuItem>
                                            );
                                        })}
                                    </Select>
                                </Grid>
                                <Grid item lg={5} sm={5} xl={5}>
                                    <InputLabel>Categoria do Processo</InputLabel>
                                    <Select
                                        fullWidth
                                        name="select-category-process"
                                        value={parameters.categoryProcessId || ''}
                                        onChange={handleOnChange}
                                    >
                                        <MenuItem value="">
                                            <em>Selecionar</em>
                                        </MenuItem>
                                        {categoryProcess.map((element) => {
                                            return (
                                                <MenuItem value={element.id.toString()} key={element.id}>
                                                    {element.id} - {element.name}
                                                </MenuItem>
                                            );
                                        })}
                                    </Select>
                                </Grid>
                                <Grid item lg={2} sm={2} xl={2}>
                                    <InputLabel>Exercício</InputLabel>
                                    <OutlinedInput
                                        name="input-year"
                                        fullWidth
                                        type="number"
                                        value={parameters.year || ''}
                                        onChange={handleOnChange}
                                        inputProps={{ min: 2000, max: new Date().getFullYear() + 1 }}
                                    />
                                </Grid>
                                <Grid item lg={12} sm={12} xl={12}>
                                    <DefaultSelectClient
                                        required={false}
                                        clientId={parameters.clientId}
                                        handleOnChange={handleOnChange}
                                    />
                                </Grid>
                                <Grid item lg={12} sm={12} xl={12}>
                                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                        <Button
                                            variant="outlined"
                                            color="secondary"
                                            startIcon={<ClearIcon />}
                                            onClick={handleClearFilters}
                                        >
                                            Limpar Filtros
                                        </Button>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            startIcon={<SearchIcon />}
                                            onClick={handleSearch}
                                        >
                                            Consultar
                                        </Button>
                                    </Box>
                                </Grid>
                            </Grid>
                        </LocalizationProvider>
                    </Collapse>
                </Paper>
            </Grid>

            <Grid item xs={12} md={12} lg={12}>
                <Paper
                    sx={{
                        p: 2,
                        minHeight: "500px",
                        display: 'flex',
                        flexDirection: 'column'
                    }}
                >
                    <ContractDataTable props={dataTableProperties} />
                    <SpeedDial
                        ariaLabel="SpeedDial basic example"
                        sx={{ position: 'absolute', bottom: 16, right: 16 }}
                        onClick={navigateToCreate}
                        icon={<SpeedDialIcon />}
                    />
                </Paper>
            </Grid>
        </React.Fragment>
    );
}
